import { defineStore } from 'pinia';
import { addTimeline, paginateUrl } from './helpers';
import axios from 'axios';
import router from '../router';
import eventBus from '../eventBus';

export const useCampaignsStore = defineStore('campaigns', {
    state: () => ({
        campaigns: []
    }),

    getters: {
        campaign: (state) => {
            return (id) => state.campaigns.find(campaign => campaign.id == id);
        },
    },

    actions: {
        _upsertCampaign(payload) {
            const idx = this.campaigns.findIndex(c => c.id === payload.id);
            if (idx !== -1) {
                this.campaigns[idx] = payload;
            } else {
                this.campaigns.push(payload);
            }
        },

        async getCampaignReportUrl(payload) {
            try {
                const response = await axios.get('/v1/campaigns/' + payload.campaign.id + '/report-url');
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async readCampaign(payload) {
            try {
                const response = await axios.get('/v1/campaigns/' + payload.campaign.id);
                let campaign = response.data.data;
                campaign = addTimeline(campaign);
                this._upsertCampaign(campaign);
                return response.data;
            } catch (error) {
                if (error.response?.status == 404) {
                    const idx = this.campaigns.findIndex(c => c.id === payload.campaign.id);
                    if (idx !== -1) this.campaigns.splice(idx, 1);
                    eventBus.emit("SHOW_ALERT", { status: 404, data: { message: "This campaign is no longer available" } });
                    await router.push('/campaigns');
                    return;
                }
                throw error.response ?? error;
            }
        },

        async readCampaigns(payload) {
            const requestUrl = paginateUrl('/v1/campaigns/?', payload.pagination);
            try {
                const response = await axios.get(requestUrl);
                const campaigns = response.data.data;
                campaigns.forEach(campaign => {
                    campaign = addTimeline(campaign);
                    this._upsertCampaign(campaign);
                });
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async createCampaign(payload) {
            try {
                const response = await axios.post("/v1/campaigns", payload.campaign);
                this.campaigns.push(response.data.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async updateCampaign(payload) {
            try {
                const response = await axios.put('/v1/campaigns/' + payload.campaign.id, payload.campaign);
                this._upsertCampaign(response.data.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async deleteCampaign(payload) {
            try {
                const response = await axios.delete("/v1/campaigns/" + payload.campaign.id);
                const idx = this.campaigns.findIndex(c => c.id === payload.campaign.id);
                if (idx !== -1) this.campaigns.splice(idx, 1);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async getCreativeFormats(payload) {
            try {
                const response = await axios.get('/v1/campaigns/' + payload.campaign.id + '/creative-formats');
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async uploadCampaignPlan(payload) {
            try {
                const response = await axios.post('/v1/campaigns/' + payload.campaignId + '/media-plan', {
                    uuid: payload.file.uuid,
                    key: payload.file.key,
                    bucket: payload.file.bucket,
                    name: payload.file.name,
                });
                return response.data;
            } catch (error) {
                throw {
                    status: error.response?.status,
                    data: error.response?.data,
                };
            }
        },

        async getLatestCampaignMediaPlan(payload) {
            try {
                const response = await axios.get('/v1/campaigns/' + payload.campaignId + '/media-plan/latest');
                return response.data;
            } catch (error) {
                throw {
                    status: error.response?.status,
                    data: error.response?.data,
                };
            }
        },

        async downloadCampaignPlan(payload) {
            try {
                const response = await axios.get('/v1/campaigns/' + payload.campaignId + '/media-plan/' + payload.mediaPlanId + '/download');
                window.open(response.data.url, '_blank', 'noopener,noreferrer');
                return response;
            } catch (error) {
                throw {
                    status: error.response?.status,
                    data: error.response?.data,
                };
            }
        },
    },

});
